import dayjs from 'dayjs';
import { Flex, Spinner } from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { fetchBlockTimestamp } from 'api/blockchain/blockTimestamp';
import { useWeb3React } from 'hooks/web3';
import {
  usePrograms,
  useProgramsRewards,
  useProgramsStaked
} from 'hooks/query/usePrograms';
import RewardProgramCarousel from 'components/rewardPrograms/RewardProgramCarousel';
import { RewardSchedule } from 'types';
import { BigNumber } from 'ethers';

type Props = {
  rewardProgramAddress: string;
  rewardSchedules: RewardSchedule[];
};

const RewardProgramInformation: React.FC<Props> = ({
  rewardProgramAddress,
  rewardSchedules
}) => {
  const { chainId = 1 } = useWeb3React();
  const [blockTimestamp, setBlockTimestamp] = useState<number>(0);

  useEffect(() => {
    fetchBlockTimestamp(chainId).then((ts) => {
      setBlockTimestamp(ts);
    });
  }, [chainId]);

  const { data: programsData } = usePrograms();
  const { data: totalRewardsData } = useProgramsRewards();
  const { data: totalStakedData } = useProgramsStaked();

  const currentRewardProgram = useMemo(() => {
    if (programsData) {
      return programsData.find(
        (program) => program.rewardProgramAddress === rewardProgramAddress
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programsData, rewardProgramAddress]);

  const lastFundedDate = useMemo(() => {
    if (rewardSchedules && rewardSchedules.length > 0) {
      let lastFunded = BigNumber.from(0);
      for (const idx in rewardSchedules) {
        if (rewardSchedules[idx][1].gt(lastFunded))
          lastFunded = rewardSchedules[idx][1];
      }
      return new Date(lastFunded.toNumber() * 1000).toLocaleDateString();
    } else {
      return 'not yet';
    }
  }, [rewardSchedules]);

  if (
    !totalStakedData ||
    !totalRewardsData ||
    !currentRewardProgram ||
    !blockTimestamp
  ) {
    return (
      <Flex
        bg='gray.700'
        alignItems='center'
        justifyContent='center'
        borderRadius='xl'
        height='100px'
      >
        <Spinner mx='auto' />
      </Flex>
    );
  }

  const { rewardProgram, apy, rewardMultiplier, rewardScalingDays } =
    currentRewardProgram;

  const getDaysLeft = () => {
    return dayjs
      .unix(rewardProgram.expiryTimestamp as number)
      .isBefore(dayjs.unix(blockTimestamp))
      ? 'Ended'
      : dayjs
          .unix(rewardProgram.expiryTimestamp as number)
          .diff(dayjs.unix(blockTimestamp), 'days') + ' days left';
  };

  const boxProps = {
    borderRadius: 'xl',
    bg: 'gray.700'
  };

  const stackProps = {
    px: [5, null],
    py: 6
  };

  return (
    <RewardProgramCarousel
      showMaintainer
      showDivider
      maintainer={rewardProgram.maintainer}
      owner={rewardProgram.owner}
      apy={apy}
      rewardMultiplier={rewardMultiplier}
      rewardScalingTime={rewardScalingDays}
      expiryTimestamp={rewardProgram.expiryTimestamp}
      daysLeft={getDaysLeft()}
      programRewardsUSD={totalRewardsData[rewardProgram.address]}
      programStakedUSD={totalStakedData[rewardProgram.address]}
      boxProps={boxProps}
      stackProps={stackProps}
      lastFundedDate={lastFundedDate}
    />
  );
};

export default RewardProgramInformation;
